import { loggerService } from "./loggerService";

const DB_NAME = "mfa-errors-db";
const DB_VERSION = 1;
const STORE_NAME = "errors";

const MAX_DAYS = 30;
const MAX_LOGS = 5000;

export interface MfaErrorLog {
    id?: number;
    date: string;
    timestamp: string;
    url?: string;
    method?: string;
    status: number;
    message?: string;
    payload?: unknown
    responseData?: unknown;
}

const openDatabase = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;

            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, {
                    keyPath: "id",
                    autoIncrement: true
                });

                store.createIndex("timestamp", "timestamp");
                store.createIndex("date", "date");
            }
        };

        request.onsuccess = () => {
            resolve(request.result);
        };

        request.onerror = () => {
            reject(request.error);
        };
    });
};

export const saveMfaError = async (
    error: Omit<MfaErrorLog, "id" | "date" | "timestamp">
): Promise<void> => {
    try {
        if (error.url?.toLowerCase().includes("mfalogs")) return;

        const now = new Date();
        const payloadToLog =
            error.payload instanceof FormData
                ? Object.fromEntries(
                    Array.from((error.payload as FormData).entries()).map(
                        ([key, value]) => [
                            key,
                            value instanceof File
                                ? {
                                    name: value.name,
                                    size: value.size,
                                    type: value.type,
                                    lastModified: value.lastModified
                                }
                                : value
                        ] as const
                    )
                )
                : error.payload;

        const log: MfaErrorLog = {
            ...error,
            payload: payloadToLog,
            date: now.toISOString().slice(0, 10),
            timestamp: now.toISOString()
        };

        const db = await openDatabase();

        await new Promise<void>((resolve, reject) => {
            const transaction = db.transaction(STORE_NAME, "readwrite");
            const store = transaction.objectStore(STORE_NAME);

            store.add(log);

            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
        });

        db.close();

        await cleanupMfaLogs();
        //* upload log to 3nom db
        const email = JSON.parse(localStorage.getItem("user") || '').email;

        await loggerService.uploadLog({
            endPName: error.url || "",
            errorCode: error.status.toString(),
            errorDate: log.timestamp,
            userEmail: email || "",
            payload: JSON.stringify(error.payload || {}),
            response: JSON.stringify(error.responseData || {}),
            description: error.message || ""
        })


    } catch (error) {
        console.error("Could not save MFA error locally", error);
    }
};

const cleanupMfaLogs = async (): Promise<void> => {
    try {
        const db = await openDatabase();

        const logs = await new Promise<MfaErrorLog[]>((resolve, reject) => {
            const transaction = db.transaction(STORE_NAME, "readonly");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.index("timestamp").getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });

        const now = Date.now();
        const maxAge = MAX_DAYS * 24 * 60 * 60 * 1000;

        const logsToDelete = logs.filter((log) => {
            const timestamp = new Date(log.timestamp).getTime();

            return now - timestamp > maxAge;
        });

        logs.sort(
            (a, b) =>
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime()
        );

        const excessLogs = Math.max(0, logs.length - MAX_LOGS);

        const idsToDelete = [
            ...logsToDelete.map((log) => log.id),
            ...logs.slice(0, excessLogs).map((log) => log.id)
        ].filter((id): id is number => id !== undefined);

        if (idsToDelete.length > 0) {
            await new Promise<void>((resolve, reject) => {
                const transaction = db.transaction(STORE_NAME, "readwrite");
                const store = transaction.objectStore(STORE_NAME);

                idsToDelete.forEach((id) => {
                    store.delete(id);
                });

                transaction.oncomplete = () => resolve();
                transaction.onerror = () => reject(transaction.error);
            });
        }

        db.close();
    } catch (error) {
        console.error("Could not cleanup MFA logs", error);
    }
};

export const exportMfaLogs = async (): Promise<void> => {
    try {
        const db = await openDatabase();

        const logs = await new Promise<MfaErrorLog[]>((resolve, reject) => {
            const transaction = db.transaction(STORE_NAME, "readonly");
            const store = transaction.objectStore(STORE_NAME);
            const request = store.index("timestamp").getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });

        db.close();

        logs.sort(
            (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
        );

        const content = logs
            .map((log) => {
                const responseContent =
                    typeof log.responseData === "string"
                        ? log.responseData
                        : JSON.stringify(log.responseData);
                const payloadContent =
                    typeof log.payload === "string"
                        ? log.payload
                        : JSON.stringify(log.payload);

                return [
                    `(date): ${log.timestamp}`,
                    '-------------------------',
                    `(url): ${log.url || ""}`,
                    '-------------------------',
                    `(method): ${log.method || ""}`,
                    '-------------------------',
                    `(status): ${log.status}`,
                    '-------------------------',
                    `(message): ${log.message || ""}`,
                    '-------------------------',
                    `(payload): ${payloadContent || ""}`,
                    '-------------------------',
                    `(content): ${responseContent || ""}`
                ].join("\n");
            })
            .join("\n\n***************************************************************************************************************************************************\n\n");

        const currentDate = new Date()
            .toISOString()
            .slice(0, 10);

        const fileName = `mfa-error-logs-${currentDate}.log`;

        const blob = new Blob([content], {
            type: "text/plain;charset=utf-8"
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Could not export MFA logs", error);
    }
};